import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Trash2, MessageSquareText, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { CopyField, botUrl, embedSnippet } from "@/components/bots/BotCreateDialog";
import { UserAvatar } from "@/components/UserAvatar";

function BotLog({ bot }) {
  const [turns, setTurns] = useState(null);
  useEffect(() => { api.get(`/bots/${bot.id}/log?limit=100`).then((r) => setTurns(r.data)).catch(() => setTurns([])); }, [bot.id]);
  if (turns === null) return <p className="text-xs text-muted-foreground">Loading…</p>;
  if (!turns.length) return <p data-testid={`bot-log-empty-${bot.id}`} className="text-xs text-muted-foreground">No questions yet. Share the link to get the first one.</p>;
  return (
    <div data-testid={`bot-log-${bot.id}`} className="space-y-3 max-h-72 overflow-y-auto pr-1">
      {turns.map((t) => (
        <div key={t.id} className="border border-border p-3 text-sm space-y-1.5">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <UserAvatar name={t.visitor?.name} picture={t.visitor?.picture} size={20} testId={`bot-log-avatar-${t.id}`} />
            <span className="font-medium text-foreground">{t.visitor?.name}</span>
            <span>· {new Date(t.created_at).toLocaleString()}</span>
            {t.model_label && <span className="ml-auto font-mono-label">{t.auto ? "Auto → " : ""}{t.model_label}</span>}
          </div>
          <p className="font-medium">{t.question}</p>
          <p className="text-muted-foreground whitespace-pre-wrap line-clamp-4">{t.answer || "—"}</p>
        </div>
      ))}
    </div>
  );
}

function BotRow({ bot, onChanged }) {
  const [open, setOpen] = useState(false);
  const toggle = async (v) => {
    try { await api.patch(`/bots/${bot.id}`, { enabled: v }); onChanged(); toast.success(v ? "Chatbot is live" : "Chatbot paused"); }
    catch (err) { toast.error(errText(err, "Couldn't update")); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${bot.name}"? The link will stop working and its question log is removed.`)) return;
    try { await api.delete(`/bots/${bot.id}`); onChanged(); toast.success("Chatbot deleted"); }
    catch (err) { toast.error(errText(err, "Couldn't delete")); }
  };
  return (
    <div data-testid={`bot-row-${bot.id}`} className={`border p-4 space-y-3 ${bot.enabled ? "border-border" : "border-dashed border-border opacity-80"}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid={`bot-name-${bot.id}`} className="font-medium truncate">{bot.name}</span>
            <span className="font-mono-label border border-border px-1.5 py-0.5 text-muted-foreground">{bot.model === "auto" ? "Auto" : bot.model}</span>
            {!bot.enabled && <span data-testid={`bot-paused-${bot.id}`} className="font-mono-label px-1.5 py-0.5 bg-muted text-muted-foreground">Paused</span>}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{bot.documents.map((d) => d.filename).join(" · ")}</p>
          <p data-testid={`bot-stats-${bot.id}`} className="text-xs text-muted-foreground mt-1">{bot.questions} question{bot.questions === 1 ? "" : "s"} · {bot.visitors} visitor{bot.visitors === 1 ? "" : "s"}</p>
        </div>
        <Switch data-testid={`bot-enabled-${bot.id}`} checked={bot.enabled} onCheckedChange={toggle} aria-label="Chatbot enabled" />
      </div>
      <CopyField value={botUrl(bot.token)} testId={`bot-link-${bot.id}`} />
      <div className="flex items-center gap-1 flex-wrap">
        <Button asChild variant="ghost" size="sm" className="rounded-sm"><a href={botUrl(bot.token)} target="_blank" rel="noreferrer" data-testid={`bot-open-${bot.id}`}><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open</a></Button>
        <Button variant="ghost" size="sm" data-testid={`bot-log-toggle-${bot.id}`} onClick={() => setOpen((o) => !o)} className="rounded-sm">
          <MessageSquareText className="w-3.5 h-3.5 mr-1.5" /> Question log {open ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
        </Button>
        <Button variant="ghost" size="sm" data-testid={`bot-delete-${bot.id}`} onClick={remove} className="rounded-sm text-destructive ml-auto"><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-border pt-3">
          <CopyField label="Embed on your site" value={embedSnippet(bot.token)} testId={`bot-embed-${bot.id}`} />
          <BotLog bot={bot} />
        </div>
      )}
    </div>
  );
}

export default function BotsDialog({ refreshKey, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [bots, setBots] = useState(null);
  const load = () => api.get("/bots").then((r) => setBots(r.data)).catch(() => toast.error("Couldn't load chatbots"));
  useEffect(() => { if (open) load(); }, [open, refreshKey]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="dashboard-bots" className="rounded-sm"><Bot className="w-4 h-4 mr-2" /> Chatbots</Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="bots-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Your chatbots</DialogTitle>
        </DialogHeader>
        {bots === null ? <p className="text-sm text-muted-foreground">Loading…</p> : bots.length === 0 ? (
          <div data-testid="bots-empty" className="border border-dashed border-border p-6 text-center space-y-2">
            <Bot className="w-8 h-8 mx-auto text-primary" strokeWidth={1.4} />
            <p className="text-sm">No chatbots yet.</p>
            <p className="text-xs text-muted-foreground">Tick one or more documents in your library and press “Publish chatbot”. Visitors sign in to DocuMind and get 20 questions a day.</p>
          </div>
        ) : (
          <div className="space-y-3">{bots.map((b) => <BotRow key={b.id} bot={b} onChanged={load} />)}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
